import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CardKinds } from './cardTemplateDbSlice';
import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

export type CardBaseProps = {
  sheetId: string;
  cardTemplateId: string;
  kind: CardKinds;
  defaultWidth: number;
  defaultHeight: number;
  frame: { display: boolean, color: string };
  background: { display: boolean, color: string };
  shadow: boolean;
  isGroup: boolean;
  groupParent: string | undefined;
  currentWidth?: number;
  currentHeight?: number;
  orientation?: 'horizontal' | 'vertical';
  links?: string[];
  goodsId?: string;
  version?: number;
}

export type StoryCard = CardBaseProps & {
  sheetKind: 'story';
  order: number;
  headerText: string;
  parentId?: string;
}
export type MapCard = CardBaseProps & {
  sheetKind: 'map';
  x: number;
  y: number;
  z: number;
}
export type GridCard = CardBaseProps & {
  sheetKind: 'grid';
  column: number;
  row: number;
  x: number;
  y: number;
}

export type CardHavingPosition = MapCard | GridCard;

export type CardDb = StoryCard | MapCard | GridCard;

const initialBase: Omit<CardBaseProps, 'kind'> = {
  sheetId: '',
  cardTemplateId: '',
  defaultWidth: 200,
  defaultHeight: 120,
  frame: { display: true, color: '#000000' },
  background: { display: true, color: '#ffffff' },
  shadow: true,
  isGroup: false,
  groupParent: undefined,
  currentWidth: undefined,
  currentHeight: undefined,
  orientation: undefined,
  links: undefined,
  goodsId: undefined,
  version: undefined
};
type CardInitialType = CardDb[]
const initial: CardInitialType = [
  {
    ...initialBase,
    kind: 'common',
    sheetKind: 'story',
    order: 0,
    headerText: '',
    parentId: undefined
  },
  {
    ...initialBase,
    kind: 'common',
    sheetKind: 'map',
    x: 0,
    y: 0,
    z: 0
  },
  {
    ...initialBase,
    kind: 'common',
    sheetKind: 'grid',
    column: 0,
    row: 0,
    x: 0,
    y: 0
  }
]

export type CardDbHashType = {
  [key in string]: CardDb;
}

export type CardAddPayload = {
  id?: string;
} & CardDb;
export type CommonCardAddPayload = CardDb & {
  id?: string;
  kind: 'common';
}
export type MemoCardAddPayload = CardDb & {
  id?: string;
  kind: 'memo';
}
export type UiCardAddPayload = CardDb & {
  id?: string;
  kind: 'ui';
}
export type DrawCardAddPayload = CardDb & {
  id?: string;
  kind: 'draw';
}

export type CardUpdatePayload = {
  id: string;
} & (
  { [P in keyof StoryCard]?: StoryCard[P] } |
  { [P in keyof MapCard]?: MapCard[P] } |
  { [P in keyof GridCard]?: GridCard[P] }
);

export type CardDbState = {
  cards: CardDbHashType;
}

export const generateCardId = (): string => generateId('c');
export const isStoryCard = (obj: any): obj is StoryCard => {
  if (!obj) return false;
  return obj.sheetKind === 'story' || 'order' in obj || 'headerText' in obj || 'parentId' in obj;
}
export const isMapCard = (obj: any): obj is MapCard => {
  if (!obj || typeof obj !== 'object' || 'column' in obj || 'row' in obj) return false;
  return obj.sheetKind === 'map' || 'x' in obj || 'y' in obj || 'z' in obj;
}
export const isGridCard = (obj: any): obj is GridCard => {
  if (!obj || 'z' in obj) return false;
  return obj.sheetKind === 'grid' || 'column' in obj || 'row' in obj || 'x' in obj || 'y' in obj;
}
const isSameType = <T, _>(a: T, b: any): b is T => {
  return (isStoryCard(a) && isStoryCard(b))
    || (isMapCard(a) && isMapCard(b))
    || (isGridCard(a) && isGridCard(b));
}
export const isCommonCard = (obj: CardAddPayload): obj is CommonCardAddPayload => {
  return obj.kind === 'common';
}
export const isMemoCard = (obj: CardAddPayload): obj is MemoCardAddPayload => {
  return obj.kind === 'memo';
}
export const isUiCard = (obj: CardAddPayload): obj is UiCardAddPayload => {
  return obj.kind === 'ui';
}
export const isDrawCard = (obj: CardAddPayload): obj is DrawCardAddPayload => {
  return obj.kind === 'draw';
}
export const getCardBase = (card: CardDb): [CardBaseProps, { [P in keyof CardDb]?: CardDb[P] }] => {
  const { sheetId, cardTemplateId, kind, defaultWidth, defaultHeight, isGroup, groupParent, frame, background, shadow, orientation, links, ...obj } = card;
  const cardBase = { sheetId, cardTemplateId, kind, defaultWidth, defaultHeight, isGroup, groupParent, frame, background, shadow, orientation, links };
  return [cardBase, obj];
}
let initialState: CardDbState = { cards: {} };
const key = 'cards';

const cardDbSlice = createSlice({
  name: 'cardDb',
  initialState,
  reducers: {
    initializeCards(state: CardDbState) {
      state.cards = {};
    },
    addCard(state: CardDbState, action: PayloadAction<CardAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateCardId();
      state.cards[id] = obj;
      saveData(key, id, obj);
    },
    updateCard(state: CardDbState, action: PayloadAction<CardUpdatePayload>) {
      const { id, ...obj } = action.payload;
      const card = state.cards[id];
      if (card === undefined) {
        console.log(`*** Card not found: ${id}`);
      } else if (isSameType(card, obj)) {
        const newData = { ...state.cards[id], ...obj };
        state.cards[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteCard(state: CardDbState, action: PayloadAction<CardUpdatePayload>) {
      const id = action.payload.id;
      delete state.cards[id];
      deleteData(key, id);
    },
    saveCards(state: CardDbState, action: PayloadAction<void>) {
      saveAllData(key, state.cards);
    },
    signUpCards(state: CardDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.cards, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: CardDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.cards = updatedValues;
      if (changed) saveAllData(key, state.cards);
    })
  }
})

export const { initializeCards, addCard, updateCard, deleteCard, saveCards, signUpCards } = cardDbSlice.actions;
export default cardDbSlice.reducer;
